import React, { useRef, useState } from "react";
import { CheckCircle2, XCircle, RotateCcw, Trophy, Loader2, ListChecks } from "lucide-react";
import { checkAnswer, submitQuiz } from "../../lib/knowledgeApi";
import { cn } from "../../lib/utils";

const LETTERS = ["A", "B", "C", "D"];

function Question({ q, i, state, onPick }) {
  const answered = state && state.answer !== undefined;
  return (
    <li data-testid={`kc-quiz-q-${i}`} className="border border-border rounded-sm bg-bg/50 p-4">
      <div className="flex gap-3">
        <span className="font-mono-tabular text-[11px] text-subtle mt-0.5">Q{i + 1}</span>
        <p className="text-[14px] font-medium leading-snug">{q.q}</p>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {q.options.map((o, j) => {
          const picked = state?.choice === j;
          const right = answered && state.answer === j;
          const wrong = answered && picked && !right;
          return (
            <button key={j} type="button" disabled={!!state} onClick={() => onPick(i, j)} data-testid={`kc-quiz-q-${i}-opt-${j}`}
                    data-state={right ? "correct" : wrong ? "wrong" : picked ? "picked" : "idle"}
                    className={cn("text-left flex items-start gap-2.5 px-3 py-2 border rounded-sm text-[13px] leading-snug transition-[border-color,background-color,transform]",
                      !state && "border-border hover:border-primary/60 hover:bg-primary/[0.04] active:scale-[0.99]",
                      right && "border-success bg-success/10", wrong && "border-critical bg-critical/10",
                      state && !right && !wrong && "border-border opacity-60")}>
              <span className="font-mono-tabular text-[10.5px] font-semibold text-muted mt-0.5">{LETTERS[j]}</span>
              <span className="flex-1">{o}</span>
              {right && <CheckCircle2 size={14} className="text-success shrink-0 mt-0.5" />}
              {wrong && <XCircle size={14} className="text-critical shrink-0 mt-0.5" />}
              {picked && !answered && <Loader2 size={13} className="animate-spin text-primary shrink-0 mt-0.5" />}
            </button>
          );
        })}
      </div>
      {answered && (
        <div data-testid={`kc-quiz-q-${i}-feedback`} className={cn("mt-3 text-[12.5px] leading-relaxed border-l-2 pl-3 animate-fade-in", state.correct ? "border-success" : "border-critical")}>
          <span className={cn("font-semibold", state.correct ? "text-success" : "text-critical")}>{state.correct ? "Correct. " : `Not quite — the answer is ${LETTERS[state.answer]}. `}</span>
          {state.why}
        </div>
      )}
    </li>
  );
}

function Result({ res, onRetake }) {
  return (
    <div data-testid="kc-quiz-result" data-passed={res.passed ? "yes" : "no"}
         className={cn("mt-4 p-4 rounded-sm border flex flex-wrap items-center gap-4 kc-rise", res.passed ? "border-success/40 bg-success/[0.07]" : "border-warning/40 bg-warning/[0.07]")}>
      <Trophy size={22} className={res.passed ? "text-success" : "text-warning"} />
      <div className="flex-1 min-w-[12rem]">
        <div className="font-mono-tabular text-2xl font-semibold" data-testid="kc-quiz-score">{res.score} / {res.total}</div>
        <div className="text-[12.5px] text-muted">{res.passed ? "Topic complete — saved to your progress." : "4 of 5 completes the topic. Review the explanations and try again."}</div>
      </div>
      <button data-testid="kc-quiz-retake" onClick={onRetake} className="inline-flex items-center gap-1.5 h-9 px-3 border border-border rounded-sm text-[12.5px] hover:border-primary hover:text-primary transition-colors">
        <RotateCcw size={13} /> Retake quiz
      </button>
    </div>
  );
}

// Five multiple-choice questions with instant feedback; graded server-side, 4 of 5 completes the topic.
export default function Quiz({ slug, quiz, record, onGraded }) {
  const [states, setStates] = useState({});
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const live = useRef({});
  const put = (next) => { live.current = next; setStates(next); };

  async function pick(i, j) {
    put({ ...live.current, [i]: { choice: j } });
    setError(null);
    try {
      const r = await checkAnswer(slug, i, j);
      put({ ...live.current, [i]: { choice: j, ...r } });
      const all = quiz.every((_, k) => live.current[k]?.answer !== undefined);
      if (all && !live.current.submitted) {
        live.current.submitted = true;
        const g = await submitQuiz(slug, quiz.map((_, k) => live.current[k].choice));
        setRes(g);
        onGraded(g);
      }
    } catch (e) {
      setError(e.message);
      const c = { ...live.current };
      delete c[i];
      delete c.submitted;
      put(c);
    }
  }
  const retake = () => { put({}); setRes(null); setError(null); };

  return (
    <section id="quiz" data-testid="kc-quiz" className="scroll-mt-28">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
        <h2 className="font-display text-xl font-semibold tracking-tight inline-flex items-center gap-2"><ListChecks size={18} className="text-primary" /> Check your understanding</h2>
        <span data-testid="kc-quiz-record" className="text-[11.5px] text-muted font-mono-tabular">
          {record ? `Best ${record.best}/5 · ${record.attempts} attempt${record.attempts === 1 ? "" : "s"}${record.passed ? " · complete" : ""}` : "5 questions · 4 correct completes the topic"}
        </span>
      </div>
      <ol className="space-y-3">{quiz.map((q, i) => <Question key={`${slug}-${i}`} q={q} i={i} state={states[i]} onPick={pick} />)}</ol>
      {error && <div data-testid="kc-quiz-error" className="mt-3 text-[12.5px] text-critical">{error}</div>}
      {res && <Result res={res} onRetake={retake} />}
    </section>
  );
}
